import { ApolloError, gql, useSubscription } from '@apollo/client';
import { FAIconName, useToastMessage } from '@equip.health/ui';
import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import {
  useProcessAigssEventsDispatch,
  useProcessedAigssEventsSelector,
} from '~/store/reducers/processedAigssEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AISummaryEvents } from '~/analytics/aiSummaryEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

type OnAIGSSUpdateData = {
  onAIGSSUpdate: {
    id: string;
    creationDate: string;
    status: string;
    text: string;
    appointmentId: string;
    providerId: string;
    note: {
      id: string;
      patientExternalId: string;
      appointmentExternalId: string;
      title: string;
      lastModifiedDateTime: string;
    };
  };
};

const ON_AIGSS_UPDATE_SUBSCRIPTION = gql`
  subscription OnAIGSSUpdate($providerId: String!) {
    onAIGSSUpdate(providerId: $providerId) {
      id
      creationDate
      status
      text
      appointmentId
      providerId
      note {
        id
        patientExternalId
        appointmentExternalId
        title
        lastModifiedDateTime
      }
    }
  }
`;

const GlobalAppSyncSubscriptionManager = () => {
  const { currentUser } = useGlobalContext();
  const { drawerStatus, noteId: curNoteId } = useEditNoteDrawerContext();
  const { close, createToast } = useToastMessage();
  const { logError } = useDatadog();
  const { processAigssEventId } = useProcessAigssEventsDispatch();
  const { ids: processedAigssEventIds } = useProcessedAigssEventsSelector();
  const { trackEvent } = useAnalytics();
  const {
    aigssBannersForDietaryBetaTest,
    aigssCoachNoteTemplate,
    aigssMedNoteTemplate,
  } = useFlags<LDFlags>();
  const { data } = useSubscription<OnAIGSSUpdateData>(
    ON_AIGSS_UPDATE_SUBSCRIPTION,
    {
      onError: (error: ApolloError) => {
        console.log(`AppSync onError: ${error && JSON.stringify(error)}`);
        logError(error, { logFrom: 'Apollo AppSync onError' });
      },
      shouldResubscribe: true,
      variables: { providerId: currentUser?.userId },
    },
  );

  useEffect(() => {
    if (data?.onAIGSSUpdate) {
      const isAppointmentInOpenDrawer =
        drawerStatus === 'open' && curNoteId === data.onAIGSSUpdate.note.id;
      if (isAppointmentInOpenDrawer) {
        return;
      }

      const event = data.onAIGSSUpdate;

      if (processedAigssEventIds[event.id] || event.status !== 'SUCCESS') {
        return;
      }

      // Skip toast for dietitian appointments when feature flag is OFF
      const noteTitle = event?.note?.title ?? '';
      const isDietitianNote =
        noteTitle.toLowerCase().includes('dietary') ||
        noteTitle.toLowerCase().includes('dietitian');
      if (!aigssBannersForDietaryBetaTest && isDietitianNote) {
        return;
      }

      // Skip toast for coach appointments when feature flag is OFF. Matches on
      // title because the subscription payload carries no template code.
      const isCoachNote = noteTitle.toLowerCase().includes('coach');
      if (!aigssCoachNoteTemplate && isCoachNote) {
        return;
      }

      // Skip toast for med team appointments when feature flag is OFF. Matches
      // on title because the subscription payload carries no template code.
      const isMedTeamNote = noteTitle.toLowerCase().includes('med team');
      if (!aigssMedNoteTemplate && isMedTeamNote) {
        return;
      }

      processAigssEventId(event.id);

      const eventAppointmentId = event.appointmentId;
      const patientId = event.note.patientExternalId;
      const noteId = event.note.id;
      const forNoteTitleText = noteTitle.length > 0 ? `for ${noteTitle}` : '';
      const noteUrl = `/maud/patients/${patientId}/notes?noteId=${noteId}&appointmentId=${eventAppointmentId}&appointmentTitle=${encodeURIComponent(noteTitle)}`;
      const aigssToastId = `aigss-toast-${eventAppointmentId}`;

      createToast({
        button: {
          icon: FAIconName.externalLinkRegular,
          onClick: () => {
            trackEvent(AISummaryEvents.aiSummaryToastViewNoteClicked, {
              Source: AnalyticsSourceValues.GLOBAL,
              Location: window.location.pathname,
            });
            window.open(noteUrl, '_blank', 'noopener');
            close(aigssToastId);
          },
          text: 'Open note',
        },
        secondaryButton: {
          text: 'Dismiss',
          icon: FAIconName.XmarkRegular,
          color: 'neutral.secondary',
          onClick: () => {
            trackEvent(AISummaryEvents.aiSummaryToastDismissClicked, {
              Source: AnalyticsSourceValues.GLOBAL,
              Location: window.location.pathname,
            });
            close(aigssToastId);
          },
        },
        duration: 12000,
        icon: FAIconName.infoCircleRegular,
        id: aigssToastId,
        isFullWidth: true,
        minWidth: '800px',
        position: 'top',
        text: `An AI session summary is now available ${forNoteTitleText}`,
        variant: 'success',
      });

      trackEvent(AISummaryEvents.aiSummaryToastDisplayed, {
        Source: AnalyticsSourceValues.GLOBAL,
        Location: window.location.pathname,
      });
    }
  }, [
    data,
    curNoteId,
    drawerStatus,
    close,
    createToast,
    logError,
    processedAigssEventIds,
    processAigssEventId,
    trackEvent,
    aigssBannersForDietaryBetaTest,
    aigssCoachNoteTemplate,
    aigssMedNoteTemplate,
  ]);

  return null;
};

export default GlobalAppSyncSubscriptionManager;
